import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Sparkles, Search, RotateCw } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

interface SearchMetricsResponse {
  status: string;
  counters: {
    query_total: number;
    hyde_applied: number;
    hyde_rate: number;
    hybrid_applied: number;
    hybrid_rate: number;
    continuation_total: number;
    continuation_rate: number;
    continuation_iters: number;
    avg_continuation_iters: number;
    started_at: string;
  };
  length_distribution: Array<{ bucket: string; count: number }>;
}

/**
 * v1.4 검색 메트릭 KPI — HyDE / Hybrid / Continuation 적용율
 * 컨테이너 재시작 시 카운터 리셋되므로 'started_at' 부터의 누적값.
 */
export function SearchMetricsKpi() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery<SearchMetricsResponse>({
    queryKey: ["admin", "search-metrics"],
    queryFn: () => api.get("/admin/search-metrics"),
    refetchInterval: 60_000,
  });

  const resetMutation = useMutation({
    mutationFn: () => api.post("/admin/search-metrics/reset", {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "search-metrics"] });
    },
  });

  if (isLoading || !data) {
    return <Skeleton className="h-44" />;
  }

  const c = data.counters;
  const startedAt = c.started_at
    ? new Date(c.started_at).toLocaleString("ko-KR")
    : "기록 없음";

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-purple-600" />
            <h3 className="text-sm font-semibold">검색 기능 적용 현황</h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            onClick={() => resetMutation.mutate()}
            disabled={resetMutation.isPending}
            title="카운터 리셋"
          >
            <RotateCw className="h-3 w-3" />
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-xs text-gray-500">총 질의 수</div>
            <div className="text-lg font-semibold">{c.query_total.toLocaleString()}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">HyDE 적용율</div>
            <div className="text-lg font-semibold text-purple-600">
              {c.hyde_rate}%
              <span className="ml-1 text-xs text-gray-500">({c.hyde_applied}건)</span>
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">
              <Search className="inline h-3 w-3 mr-1" />
              Hybrid 검색율
            </div>
            <div className="text-lg font-semibold text-blue-600">
              {c.hybrid_rate}%
              <span className="ml-1 text-xs text-gray-500">({c.hybrid_applied}건)</span>
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">이어쓰기 발생율</div>
            <div className="text-lg font-semibold text-orange-600">
              {c.continuation_rate}%
              <span className="ml-1 text-xs text-gray-500">({c.continuation_total}건)</span>
            </div>
          </div>
        </div>

        <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500">
          카운터 시작: <span className="font-medium">{startedAt}</span>
        </div>

        {c.continuation_total > 0 && (
          <div className="mt-2 text-xs text-gray-600">
            평균 이어쓰기 호출: <span className="font-medium">{c.avg_continuation_iters}회</span> /
            긴 답변
          </div>
        )}
      </CardContent>
    </Card>
  );
}
